import type {
  AnchoredVolumeProfileRenderNode,
  ElliottCorrectionABCRenderNode,
  ElliottImpulseRenderNode,
  ExtensibleInfoLineRenderNode,
  FibChannelRenderNode,
  FibRetracementRenderNode,
  FixedRangeVolumeProfileRenderNode,
  GannBoxRenderNode,
  GannFanRenderNode,
  HorzLineRenderNode,
  ParallelChannelRenderNode,
  PathRenderNode,
  RectangleRenderNode,
  RenderNode,
  TextRenderNode,
  TriangleRenderNode,
  VertLineRenderNode,
  XABCDPatternRenderNode,
} from '@/types/commontypes';
import type { ShapePropsByType } from '@/types/commontypes';
import { ShapeType } from '@/types/commontypes';
import { getLineOptionAppearance, setDirection } from './setDirection';
import { fixedAppearance, defaultShapePropertyValues, volumeProfileDefaults } from './defaultShapePropertyValues';
import type { strokeDashTypes } from 'react-financial-charts';
import { isDefined } from 'react-financial-charts';
import { getIsDarkMode } from './handleColorFormat';

/**
 * RFC render mode string — 각 도형이 RFC 래퍼에 넘기는 `type` prop 값.
 * 'XLINE' | 'LINE' | 'RAY' | 'BOUND' 중 하나.
 */
export type RFCLineMode = 'XLINE' | 'LINE' | 'RAY' | 'BOUND';

/**
 * ShapeType → RFC render mode 매핑.
 * `Record<ShapeType, RFCLineMode>` 타입이 모든 ShapeType 키 포함을 컴파일 타임에 강제한다.
 *
 * **출처**: 옛 ShapeRender.tsx가 wrapper에 전달하던 `<TrendLine type="LINE"/>` 같은
 * shape별 RFC type prop 값. (wrapper의 `defaultProps.type`이 아니라 ShapeRender의 직접 전달값을
 * source of truth로 사용해야 한다 — 'XLINE'은 RFC에서 양방향 무한 확장을 의미하므로
 * 일반 라인/직사각형 도형에는 부적합)
 *
 * - `LINE`: 두 점 사이만 그림 (TrendLine, Ray, InfoLine, Rectangle 등 일반 도형)
 * - `XLINE`: 양방향 무한 확장 (HorzLine, VertLine 전용)
 * - `BOUND`: FibRetracement 전용 (양 끝 boundary)
 * - `RAY`: 한 방향 확장 (현재 사용처 없음)
 *
 * 저장되지 않거나 RFC `type`을 사용하지 않는 도형(TEXT/ORDER_LINE/MEASURE)은 placeholder 'LINE'.
 */
export const RFC_RENDER_MODE: Record<ShapeType, RFCLineMode> = {
  [ShapeType.RECTANGLE]: 'LINE',
  [ShapeType.TRIANGLE]: 'LINE',
  [ShapeType.TREND_LINE]: 'LINE',
  [ShapeType.RAY]: 'LINE',
  [ShapeType.INFO_LINE]: 'LINE',
  [ShapeType.HORZ_LINE]: 'XLINE',
  [ShapeType.VERT_LINE]: 'XLINE',
  [ShapeType.FIXED_RANGE_VOLUME_PROFILE]: 'LINE',
  [ShapeType.ANCHORED_VOLUME_PROFILE]: 'LINE',
  [ShapeType.FIB_RETRACEMENT]: 'BOUND',
  [ShapeType.FIB_CHANNEL]: 'LINE',
  [ShapeType.PARALLEL_CHANNEL]: 'LINE',
  [ShapeType.GANN_BOX]: 'LINE',
  [ShapeType.GANN_FAN]: 'LINE',
  [ShapeType.ELLIOTT_IMPULSE_12345]: 'LINE',
  [ShapeType.ELLIOTT_CORRECTION_ABC]: 'LINE',
  [ShapeType.XABCD_PATTERN]: 'LINE',
  [ShapeType.PATH]: 'LINE',
  [ShapeType.TEXT]: 'LINE',
  [ShapeType.ORDER_LINE]: 'LINE',
  [ShapeType.MEASURE]: 'LINE',
};

export function getShapeProps<T extends keyof ShapePropsByType>(
  type: T,
  shapeProps?: Partial<ShapePropsByType[T]>,
): ShapePropsByType[T] {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
  return {
    ...defaultShapePropertyValues[type],
    ...shapeProps,
  } as ShapePropsByType[T];
}

// ─────────────────────────────────────────────────────────────
// getXxxAppearance — RenderNode variant를 RFC flat props로 변환.
// 각 함수는 해당 RenderNode variant를 받아 RFC wrapper가 소비하는
// flat appearance object를 반환한다.
// ─────────────────────────────────────────────────────────────

export const getLineAppearance = (node: ExtensibleInfoLineRenderNode) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const defaults = defaultShapePropertyValues[node.type] as Partial<ExtensibleInfoLineRenderNode>;
  const props = { ...defaults, ...node };
  const isDarkMode = getIsDarkMode();

  return {
    type: RFC_RENDER_MODE[node.type],
    showPriceRange: props.showPriceRange ?? false,
    showPercentChange: props.showPercentChange ?? false,
    showBarsRange: props.showBarsRange ?? false,
    showDateTimeRange: props.showDateTimeRange ?? false,
    showAngle: props.showAngle ?? false,
    alwaysShow: props.alwaysShow ?? false,
    extendLeft: props.extend?.[0] ?? false,
    extendRight: props.extend?.[1] ?? false,
    bgStyle: isDarkMode ? 'rgba(54, 58, 69, 0.8)' : 'rgba(242, 242, 242, 0.8)',
    textColor: isDarkMode ? 'rgba(209, 212, 220, 1)' : 'rgba(29, 31, 34, 1)',
    arrowDirection: setDirection(props),
    fillStyle: undefined as string | undefined,
    middle: undefined as
      | {
          strokeStyle: string;
          strokeWidth: number;
          strokeDasharray: strokeDashTypes;
          show: boolean;
        }
      | undefined,
    ...getLineOptionAppearance(props.line),
    ...fixedAppearance,
  };
};

export const getHorzVertLineAppearance = (node: HorzLineRenderNode | VertLineRenderNode) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const defaults = defaultShapePropertyValues[node.type] as Partial<HorzLineRenderNode | VertLineRenderNode>;
  const props = { ...defaults, ...node };

  return {
    type: RFC_RENDER_MODE[node.type],
    ...getLineOptionAppearance(props.line),
    ...fixedAppearance,
  };
};

export const getFibRetracementAppearance = (node: FibRetracementRenderNode) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const defaults = defaultShapePropertyValues[ShapeType.FIB_RETRACEMENT] as Partial<FibRetracementRenderNode>;
  const props = { ...defaults, ...node };

  return {
    type: RFC_RENDER_MODE[ShapeType.FIB_RETRACEMENT],
    nsEdgeFill: 'white',
    extendLeft: props.extend?.[0] ?? false,
    extendRight: props.extend?.[1] ?? false,
    strokeDasharray: props.levelsLine?.strokeDasharray,
    strokeWidth: Number(props.levelsLine?.strokeWidth),
    retracementLevels: props.levels,
    backgroundOpacity: resolveOpacity(props.background),
    trendlineAppearance: {
      strokeStyle: props.trendLine?.strokeStyle,
      strokeDasharray: props.trendLine?.strokeDasharray,
      strokeWidth: Number(props.trendLine?.strokeWidth),
      edgeFill: 'white',
    },
    ...fixedAppearance,
  };
};

export const getFibChannelAppearance = (node: FibChannelRenderNode) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const defaults = defaultShapePropertyValues[ShapeType.FIB_CHANNEL] as Partial<FibChannelRenderNode>;
  const props = { ...defaults, ...node };

  return {
    type: RFC_RENDER_MODE[ShapeType.FIB_CHANNEL],
    edgeFill2: '#FFFFFF',
    extendLeft: props.extend?.[0] ?? false,
    extendRight: props.extend?.[1] ?? false,
    channelLevels: props.levels,
    backgroundOpacity: resolveOpacity(props.background),
    isParallel: false,
    strokeOpacity: 1,
    fill: '#ef0e6d' as string | string[],
    fillOpacity: 0.7,
    midLine: undefined as
      | {
          readonly strokeStyle: string;
          readonly strokeWidth: number;
          readonly strokeDasharray: string;
        }
      | undefined,
    ...getLineOptionAppearance(props.levelsLine),
    ...fixedAppearance,
  };
};

export const getGannboxAppearance = (node: GannBoxRenderNode) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const defaults = defaultShapePropertyValues[ShapeType.GANN_BOX] as Partial<GannBoxRenderNode>;
  const props = { ...defaults, ...node };

  return {
    type: RFC_RENDER_MODE[ShapeType.GANN_BOX],
    strokeWidth: 1,
    timeLevels: props.timeLevels,
    priceLevels: props.priceLevels,
    timeBgOpacity: resolveOpacity(props.timeBackground),
    priceBgOpacity: resolveOpacity(props.priceBackground),
    ...fixedAppearance,
  };
};

export const getGannFanAppearance = (node: GannFanRenderNode) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const defaults = defaultShapePropertyValues[ShapeType.GANN_FAN] as Partial<GannFanRenderNode>;
  const props = { ...defaults, ...node };

  return {
    type: RFC_RENDER_MODE[ShapeType.GANN_FAN],
    showFansWithColor: props.levels,
    backgroundOpacity: resolveOpacity(props.background),
    stroke: '#000000',
    strokeOpacity: 1,
    fillOpacity: 0.2,
    strokeWidth: 1,
    fill: [] as string[],
    fontFamily: "-apple-system, system-ui, Roboto, 'Helvetica Neue', Ubuntu, sans-serif",
    fontSize: 12,
    fontFill: '#000000',
    priceLevels: undefined as { show: boolean; value: number; color: string }[] | undefined,
    timeLevels: undefined as { show: boolean; value: number; color: string }[] | undefined,
    timeBgOpacity: undefined as number | undefined,
    priceBgOpacity: undefined as number | undefined,
    ...fixedAppearance,
  };
};

export const getRectangleAppearance = (node: RectangleRenderNode) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const defaults = defaultShapePropertyValues[ShapeType.RECTANGLE] as Partial<RectangleRenderNode>;
  const props = { ...defaults, ...node };

  const middleAppearance =
    props.middle?.show === false
      ? { show: false }
      : {
          strokeStyle: props.middle?.strokeStyle,
          strokeDasharray: props.middle?.strokeDasharray,
          strokeWidth: Number(props.middle?.strokeWidth),
          show: props.middle?.show ?? false,
        };
  return {
    type: RFC_RENDER_MODE[ShapeType.RECTANGLE],
    ...getLineOptionAppearance(props.border),
    extendLeft: props.extend?.[0] ?? false,
    extendRight: props.extend?.[1] ?? false,
    edgeFill: '#FFFFFF',
    r: 5,
    edgeStrokeWidth: 2,
    edgeStroke: '#1e53e6',
    fontFamily: 'Arial',
    fontSize: 12,
    fillStyle: props.background?.show === false ? 'rgba(0, 0, 0, 0)' : props.background?.color,
    fillColor: props.background?.color ?? 'rgba(156, 39, 176, 0.2)',
    middle: middleAppearance,
  };
};

export const getElloitImpulseAppearance = (node: ElliottImpulseRenderNode) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const defaults = defaultShapePropertyValues[ShapeType.ELLIOTT_IMPULSE_12345] as Partial<ElliottImpulseRenderNode>;
  const props = { ...defaults, ...node };

  return {
    type: RFC_RENDER_MODE[ShapeType.ELLIOTT_IMPULSE_12345],
    ...getLineOptionAppearance(props.line),
    ...fixedAppearance,
    fontFamily: 'Arial',
    fontSize: 12,
    arrowDirection: 'none' as 'left' | 'right' | 'both' | 'none',
    textFill: undefined as string | undefined,
    fontWeight: undefined as string | undefined,
    fontStyle: undefined as string | undefined,
    fillStyle: undefined as string | undefined,
  };
};

export const getTriangleAppearance = (node: TriangleRenderNode) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const defaults = defaultShapePropertyValues[ShapeType.TRIANGLE] as Partial<TriangleRenderNode>;
  const props = { ...defaults, ...node };

  return {
    type: RFC_RENDER_MODE[ShapeType.TRIANGLE],
    ...getLineOptionAppearance(props.border),
    ...fixedAppearance,
    extendLeft: false,
    extendRight: false,
    fillStyle: props.background?.show === false ? 'rgba(0, 0, 0, 0)' : props.background?.color,
    fillColor: props.background?.color ?? 'rgba(156, 39, 176, 0.2)',
  };
};

export const getPathAppearance = (node: PathRenderNode) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const defaults = defaultShapePropertyValues[ShapeType.PATH] as Partial<PathRenderNode>;
  const props = { ...defaults, ...node };

  return {
    type: RFC_RENDER_MODE[ShapeType.PATH],
    ...getLineOptionAppearance(props.line),
    edgeFill: '#FFFFFF',
    r: 5,
    edgeStrokeWidth: 2,
    edgeStroke: '#1e53e6',
    fontFamily: 'Arial',
    fontSize: 12,
    arrowDirection: setDirection({
      ...props,
      arrow: [props.arrow?.[0] ?? 'normal', props.arrow?.[1] ?? 'arrow'],
    }),
  };
};

export const getElliottCorrectionAppearance = (node: ElliottCorrectionABCRenderNode) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const defaults = defaultShapePropertyValues[
    ShapeType.ELLIOTT_CORRECTION_ABC
  ] as Partial<ElliottCorrectionABCRenderNode>;
  const props = { ...defaults, ...node };

  return {
    type: RFC_RENDER_MODE[ShapeType.ELLIOTT_CORRECTION_ABC],
    ...getLineOptionAppearance(props.line),
    edgeFill: '#FFFFFF',
    r: 5,
    edgeStrokeWidth: 2,
    edgeStroke: '#1e53e6',
    fontFamily: 'Arial',
    fontSize: 12,
  };
};

export const getParallelChannelAppearance = (node: ParallelChannelRenderNode) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const defaults = defaultShapePropertyValues[ShapeType.PARALLEL_CHANNEL] as Partial<ParallelChannelRenderNode>;
  const props = { ...defaults, ...node };

  const midLineAppearance = {
    show: props.middle?.show ?? false,
    strokeStyle: props.middle?.show === false ? 'rgba(0,0,0,0)' : (props.middle?.strokeStyle ?? '#2962ff'),
    originalStrokeStyle: props.middle?.strokeStyle, // show=false 시에도 원래 색상 보존
    strokeDasharray: props.middle?.strokeDasharray,
    strokeWidth: Number(props.middle?.strokeWidth),
  };

  return {
    type: RFC_RENDER_MODE[ShapeType.PARALLEL_CHANNEL],
    ...getLineOptionAppearance(props.channel),
    ...fixedAppearance,
    extendLeft: props.extend?.[0] ?? false,
    extendRight: props.extend?.[1] ?? false,
    arrowDirection: setDirection(null),
    fill: resolveFillStyle(props.background, 'rgba(156, 39, 176, 0.05)'),
    fillColor: props.background?.color ?? 'rgba(156, 39, 176, 0.05)',
    edgeFill2: '#FFFFFF',
    isParallel: true,
    midLine: midLineAppearance,
  };
};

export const getXabcdAppearance = (node: XABCDPatternRenderNode) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const defaults = defaultShapePropertyValues[ShapeType.XABCD_PATTERN] as Partial<XABCDPatternRenderNode>;
  const props = { ...defaults, ...node };

  return {
    type: RFC_RENDER_MODE[ShapeType.XABCD_PATTERN],
    ...getLineOptionAppearance(props.border),
    ...fixedAppearance,
    fontSize: props.label?.fontSize ?? 12,
    fontFamily: props.label?.fontFamily ?? 'Arial',
    fontWeight: props.label?.fontWeight ?? 'normal',
    fontStyle: props.label?.fontStyle ?? 'normal',
    textFill: props.label?.fill,
    fillStyle: resolveFillStyle(props.background, 'rgba(156, 39, 176, 0.05)'),
    fillColor: props.background?.color ?? 'rgba(156, 39, 176, 0.05)',
  };
};

export const getTextAppearance = (node: TextRenderNode) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const defaults = defaultShapePropertyValues[ShapeType.TEXT] as Partial<TextRenderNode>;
  const props = { ...defaults, ...node };

  return {
    type: RFC_RENDER_MODE[ShapeType.TEXT],
    fontSize: props.font?.fontSize ?? 12,
    fontFamily: props.font?.fontFamily ?? 'Arial',
    fontWeight: String(props.font?.fontWeight ?? 'normal'),
    fontStyle: props.font?.fontStyle ?? 'normal',
    textFill: props.font?.fill,
    showBackground: props.background?.show ?? true,
    bgFill: props.background?.color,
    bgOpacity: 1,
    bgStrokeWidth: 1,
    bgStroke: undefined as string | undefined,
  };
};

export const resolveOpacity = (background: { show: unknown; opacity: number } | undefined, fallback = 0.2) => {
  if (!background) return undefined;
  const { show, opacity } = background;
  if (show === false) return 0;
  return isDefined(opacity) ? opacity : fallback;
};
export const resolveFillStyle = (
  background: { show: boolean; color: string } | undefined,
  defaultColor: string = 'rgba(156, 39, 176, 0.05)',
) => {
  if (!background) return undefined;
  const { show, color } = background;
  if (show === false) return 'rgba(0, 0, 0, 0)';
  return isDefined(color) ? color : defaultColor;
};

export const getVolumeProfileAppearance = (
  node: FixedRangeVolumeProfileRenderNode | AnchoredVolumeProfileRenderNode,
) => {
  const props = { ...volumeProfileDefaults, ...node };

  return {
    type: RFC_RENDER_MODE[node.type],
    ...volumeProfileDefaults,
    rowSize: props.rowSize,
    valueAreaVolume: props.valueAreaVolume,
    values: props.values,
    width: props.width,
    placement: props.placement,
    upVolume: props.upVolume,
    downVolume: props.downVolume,
    histogramBox: props.histogramBox,
    vah: props.vah,
    val: props.val,
    poc: props.poc,
    volumeProfile: node.volumeProfile ?? true,
    ...fixedAppearance,
  };
};

// ─────────────────────────────────────────────────────────────
// WeakMap memoization — 동일 RenderNode 참조에 대해 동일 객체를 반환.
// zustand immutable update 패턴과 결합 시 편집되지 않은 노드는
// 100% cache hit이 되어 불필요한 object allocation 및 하위 React 리렌더를 억제한다.
// ─────────────────────────────────────────────────────────────
// dark/light 이중 캐시 — getIsDarkMode()에 의존하는 함수(getLineAppearance 등)도
// 순수성을 유지하도록 캐시 키를 사실상 (isDark, node)로 확장한다.
// 다크 토글 직후 첫 호출만 반대편 캐시가 비어 있고, 이후 호출은 각각의 캐시에서 히트한다.
function memoByNode<N extends object, R>(fn: (n: N) => R): (n: N) => R {
  const lightCache = new WeakMap<N, R>();
  const darkCache = new WeakMap<N, R>();
  return n => {
    const cache = getIsDarkMode() ? darkCache : lightCache;
    const cached = cache.get(n);
    if (cached !== undefined) return cached;
    const value = fn(n);
    cache.set(n, value);
    return value;
  };
}

export const toLineRFCProps = memoByNode(getLineAppearance);
export const toHorzVertLineRFCProps = memoByNode(getHorzVertLineAppearance);
export const toFibRetracementRFCProps = memoByNode(getFibRetracementAppearance);
export const toFibChannelRFCProps = memoByNode(getFibChannelAppearance);
export const toGannboxRFCProps = memoByNode(getGannboxAppearance);
export const toGannFanRFCProps = memoByNode(getGannFanAppearance);
export const toRectangleRFCProps = memoByNode(getRectangleAppearance);
export const toElloitImpulseRFCProps = memoByNode(getElloitImpulseAppearance);
export const toTriangleRFCProps = memoByNode(getTriangleAppearance);
export const toPathRFCProps = memoByNode(getPathAppearance);
export const toElliottCorrectionRFCProps = memoByNode(getElliottCorrectionAppearance);
export const toParallelChannelRFCProps = memoByNode(getParallelChannelAppearance);
export const toXabcdRFCProps = memoByNode(getXabcdAppearance);
export const toTextRFCProps = memoByNode(getTextAppearance);
export const toVolumeProfileRFCProps = memoByNode(getVolumeProfileAppearance);

/**
 * Unified dispatcher: RenderNode → appearance for any shape type.
 * 내부에서 memoized 어댑터로 위임하여 동일 노드 참조에 대해 동일 결과를 재사용한다.
 */
export function convertToAppearance(node: RenderNode): Record<string, unknown> {
  switch (node.type) {
    case ShapeType.TREND_LINE:
    case ShapeType.RAY:
    case ShapeType.INFO_LINE:
      return toLineRFCProps(node);
    case ShapeType.HORZ_LINE:
      return toHorzVertLineRFCProps(node);
    case ShapeType.VERT_LINE:
      return toHorzVertLineRFCProps(node);
    case ShapeType.FIB_RETRACEMENT:
      return toFibRetracementRFCProps(node);
    case ShapeType.FIB_CHANNEL:
      return toFibChannelRFCProps(node);
    case ShapeType.GANN_BOX:
      return toGannboxRFCProps(node);
    case ShapeType.GANN_FAN:
      return toGannFanRFCProps(node);
    case ShapeType.RECTANGLE:
      return toRectangleRFCProps(node);
    case ShapeType.TRIANGLE:
      return toTriangleRFCProps(node);
    case ShapeType.PARALLEL_CHANNEL:
      return toParallelChannelRFCProps(node);
    case ShapeType.ELLIOTT_IMPULSE_12345:
      return toElloitImpulseRFCProps(node);
    case ShapeType.ELLIOTT_CORRECTION_ABC:
      return toElliottCorrectionRFCProps(node);
    case ShapeType.XABCD_PATTERN:
      return toXabcdRFCProps(node);
    case ShapeType.PATH:
      return toPathRFCProps(node);
    case ShapeType.TEXT:
      return toTextRFCProps(node);
    case ShapeType.FIXED_RANGE_VOLUME_PROFILE:
    case ShapeType.ANCHORED_VOLUME_PROFILE:
      return toVolumeProfileRFCProps(node);
    default:
      return {};
  }
}
